/* eslint-disable consistent-return */
import React, { FC, useEffect, useMemo } from 'react';

import { Box } from '@mui/system';

import {
  AdminLegendVariant,
  PharmacistLegendVariant,
  ProfessionalReplacementTypes,
  ServiceTypes,
  TypeOfBill,
  TypeOfSummary,
  adminActions,
  adminBookedPharmacistDetails,
  adminBookingDetails,
  convertToDecimals,
  userPreferredTimeFormat,
} from '@pharmaplan/common';
import { ReactComponent as Calendar } from '@pharmaplan/common/assets/icons/calendarIconNew.svg';
import ServiceHandler, {
  idStore,
} from '@pharmaplan/common/helpers/ServiceHandler';
import strings from '../../../../localization';
import ServiceCard from '../../../SelfService/ServiceCard';
import CustomGridLayout from '../../../common/CustomGridLayout';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { Constants, ReplacementLabels, ScreenTypes } from '../../../../helpers/Constants';
import ThemeConstants from '../../../../theme/ThemeConstants';
import { getDayMonthDateYear } from '../../../Reports/helpers';
import { getHourMinuteSeconds } from '../../../../helpers/Functions';
import PharmacistCard from '../../../SelfService/PharmacistCard';
import SoftwareExpertise from '../../../SelfService/SoftwareExpertise';
import PharmacistSummary from '../../../SelfService/PharmacistSummary';
import BillDetails from '../../../SelfService/BillDetails';
import {
  renderScreen,
  setBack,
  setFooterShow,
} from '../../../../actions/drawerActions';
import useDetailsPagination from '../../../../hooks/Admin/useDetailsPagination';
import { getMultipleEventAdd } from '../../../../selectors/calendarSelector';
import ViewAllLongTermContracts from '../../../ViewAllLongTermContracts';
import useOptimizedAvatar from '../../../../hooks/useOptimizedAvatar';
import TotalContractValue from '../../../common/TotalContractValue';
import { setDialog } from '../../../../reducers/dialogReducer';
import NoteLabelValue from '../../../common/NoteLabelValue';
import useGetItems from '../../../../hooks/useGetItems';
import CustomButton from '../../../common/CustomButton';

import styles from './styles';

const { self, full } = ServiceTypes;
interface IViewBookedDetails {
  bookingId: string;
  bookingIdList?: Array<string> | null;
  isExternalPharmacist?: boolean;
  isAutoSyncRecord?: boolean;
}

const {
  getBookedPharmacistDetails,
  adminPharmacistPictures: adminPharmacistPicturesAction,
} = adminActions;

const ViewBookedDetails: FC<IViewBookedDetails> = ({
  bookingId,
  bookingIdList,
  isAutoSyncRecord,
  isExternalPharmacist,
}) => {
  const serviceTypes = ServiceHandler.getService();

  const serviceBasedConfig = {
    [full]: {
      title: strings.pharmacistDetails,
    },
    [self]: {
      title: strings.professionalDetails,
    },
  };

  const dispatch = useAppDispatch();
  const enabledMultipleAdd = useAppSelector(getMultipleEventAdd);

  const { id } = useDetailsPagination(bookingIdList);
  const { getPharmacistImage } = useGetItems();
  const { title } = serviceBasedConfig[serviceTypes];

  const isNotFromMap = !id;
  const eventId = id ?? bookingId;

  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const event = useAppSelector(adminBookingDetails);
  const {
    experience,
    createDate,
    bookingCost,
    email,
    address,
    province,
    softwares,
    startDate,
    postalCode,
    licenseNumber,
    city,
    replacementType,
    longTermShiftContract,
    contractCost,
    professionalBookedCount,
  } = event;

  const { longTermShiftContractId, allowanceNotes } = longTermShiftContract ?? {};

  const userId = event[idStore.id as keyof typeof event] as string;
  const replacement = ReplacementLabels[
    replacementType ?? ProfessionalReplacementTypes.pharmacist
  ];
  const profilePicture = getPharmacistImage(userId);

  const goToBookingList = () => {
    dispatch(
      renderScreen({
        screenNumber: 1,
        screenType: ScreenTypes.bookingList,
        date: startDate,
      }),
    );
  };

  const goToBookings = () => {
    dispatch(
      setDialog({
        heading: {
          title: strings.bookingDetails,
        },
        Component: (
          <ViewAllLongTermContracts
            type={PharmacistLegendVariant.booked}
            longTermContractId={longTermShiftContractId ?? ''}
            professionalId={event?.[idStore.id as keyof typeof event] as string}
          />
        ),
        maxWidth: 'lg',
        showCloseButton: true,
      }),
    );
  };

  useEffect(() => {
    dispatch(adminBookedPharmacistDetails({ id: eventId, isAutoSyncRecord }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (enabledMultipleAdd) {
      dispatch(setFooterShow(false));
      dispatch(setBack(null));
      return;
    }
    if (isNotFromMap) {
      dispatch(setBack(goToBookingList));
      dispatch(setFooterShow(false));
    }
    return () => {
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  const pharmacistDetails = [
    { key: strings.yearsOfExperience, value: `${experience}` },
    { key: strings.licenseNo, value: licenseNumber },
    { key: strings.email, value: email },
    {
      key: strings.address,
      value: `${address}, ${city}, ${province}, ${postalCode}`,
    },
    {
      key: strings.formatString(strings.createdFormat, strings.date) as string,
      value: getDayMonthDateYear(createDate),
      adornments: <Calendar fill={ThemeConstants.common.grey850} />,
    },
    {
      key: strings.formatString(strings.createdFormat, strings.time) as string,
      value: getHourMinuteSeconds(createDate, timeFormat),
    },
  ];

  const fullEvent = {
    ...event,
    cost: bookingCost,
  };

  const pIds = useMemo(() =>
    [userId], [userId]);

  useOptimizedAvatar(pIds);

  return (
    <Box sx={styles.container}>
      <PharmacistCard
        eventIds={bookingIdList ?? []}
        eventId={eventId}
        type={PharmacistLegendVariant.booked}
        pharmacistDetails={{
          ...event,
          profilePicture,
        }}
        screenType={ScreenTypes.pharmacistProfile}
        successAction={getBookedPharmacistDetails}
        imageSuccessAction={adminPharmacistPicturesAction}
        customFooterEnd={
          (event)?.longTermShiftContractId ? (
            <CustomButton
              customButtonStyle={styles.counterOfferBtn}
              customClass={styles.counterOfferContainer}
              onClick={goToBookings}
              label={strings.viewAllBookings}
              variant={Constants.variant.outlined}
            />
          ) : null
        }
        showRating={!isAutoSyncRecord && !isExternalPharmacist}
        isExternalPharmacist={isAutoSyncRecord || isExternalPharmacist}
      />
      <ServiceCard
        customContainerStyle={styles.serviceContainer}
        content={(
          <CustomGridLayout
            successAction={getBookedPharmacistDetails}
            title={title}
            config={pharmacistDetails}
          />
        )}
      />
      <SoftwareExpertise
        softwareDetails={softwares}
        successAction={getBookedPharmacistDetails}
      />
      <PharmacistSummary
        event={event}
        summaryType={TypeOfSummary.workshiftDetails}
        type={AdminLegendVariant.requested}
        isAutoSyncRecord={isAutoSyncRecord}
        isLongTerm={!!longTermShiftContractId}
      />
      <BillDetails
        successAction={getBookedPharmacistDetails}
        billType={TypeOfBill.finalBill}
        event={fullEvent}
        customTitle={replacement}
        isAutoSyncRecord={isAutoSyncRecord}
      />

      {!!longTermShiftContractId && (
        <TotalContractValue
          days={professionalBookedCount ?? 0}
          value={convertToDecimals(contractCost) as number}
        />
      )}

      {!!allowanceNotes && (
        <ServiceCard
          customContainerStyle={styles.padTop}
          content={
            <NoteLabelValue label={strings.allowances} value={allowanceNotes} />
          }
        />
      )}
    </Box>
  );
};

export default ViewBookedDetails;
